import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Tooltip, Typography, LinearProgress } from '@mui/material';
import { getPhaseProgress } from '../utils/api';
import { getProjectTimeline } from '../utils/preparePhaseService';

const PHASES = [
  { key: 'prepare', label: 'Prepare', color: '#F5821F', path: 'prepare', firstSubpath: 'workshop-schedule' },
  { key: 'explore', label: 'Explore', color: '#4DB6AC', path: 'explore', firstSubpath: 'gap-analysis' },
  { key: 'realize', label: 'Realize', color: '#9C27B0', path: 'realize', firstSubpath: 'ricefw-list' },
  { key: 'deploy',  label: 'Deploy',  color: '#FF5722', path: 'deploy',  firstSubpath: 'integration-testing' },
  { key: 'run',     label: 'Run',     color: '#00BCD4', path: 'run',     firstSubpath: 'hypercare' },
];

const calcTimelinePct = (startDate, endDate) => {
  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  const total = end - start;
  if (total <= 0) return null;
  return Math.min(100, Math.max(0, Math.round(((today - start) / total) * 100)));
};

const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const PhaseProgressPipeline = ({ projectId }) => {
  const navigate = useNavigate();
  const { projectId: routeProjectId } = useParams();
  const pid = projectId || routeProjectId;

  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [timelineMap, setTimelineMap] = useState({});

  const fetchProgress = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const data = await getPhaseProgress(pid);
      if (data.success) setProgress(data.progress);
    } catch (e) {
      // silently ignore — non-critical
    } finally {
      setLoading(false);
    }
  }, [pid]);

  const fetchTimeline = useCallback(async () => {
    if (!pid) return;
    try {
      const data = await getProjectTimeline(pid);
      if (data && data.phases?.length) {
        const map = {};
        data.phases.forEach((p) => {
          const key = p.name?.toLowerCase();
          if (key) map[key] = p;
        });
        setTimelineMap(map);
      }
    } catch (e) {
      // silently ignore — non-critical
    }
  }, [pid]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);
  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  if (!pid) return null;

  const hasTimeline = Object.keys(timelineMap).length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        height: hasTimeline ? 56 : 36,
        position: 'sticky', top: '64px', zIndex: 1099, bgcolor: '#0d1117',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        overflow: 'hidden',
        transition: 'height 0.3s ease',
      }}
    >
      {PHASES.map((phase, idx) => {
        const pct = progress[phase.key] ?? null;
        const isLoaded = pct !== null;
        const color = phase.color;
        const tlPhase = timelineMap[phase.key];
        const tlPct = tlPhase ? calcTimelinePct(tlPhase.startDate || tlPhase.start_date, tlPhase.endDate || tlPhase.end_date) : null;

        return (
          <React.Fragment key={phase.key}>
            {/* Arrow connector */}
            {idx > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 1 }}>
                <Box sx={{
                  width: 0, height: 0,
                  borderTop: `${hasTimeline ? 28 : 18}px solid transparent`,
                  borderBottom: `${hasTimeline ? 28 : 18}px solid transparent`,
                  borderLeft: '12px solid #0d1117',
                }} />
              </Box>
            )}

            {/* Phase segment */}
            <Tooltip
              title={
                <Box sx={{ textAlign: 'center', p: 0.5 }}>
                  <Typography variant="caption" fontWeight={700}>{phase.label} Phase</Typography>
                  <Typography variant="caption" display="block">
                    {isLoaded ? `${pct}% tasks complete` : 'Loading…'}
                  </Typography>
                  {tlPct !== null && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                      {tlPct}% of timeline elapsed
                    </Typography>
                  )}
                  {tlPhase && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.6, mt: 0.5 }}>
                      {fmt(tlPhase.startDate || tlPhase.start_date)} – {fmt(tlPhase.endDate || tlPhase.end_date)}
                    </Typography>
                  )}
                </Box>
              }
              arrow
              placement="bottom"
            >
              <Box
                onClick={() => navigate(`/projects/${pid}/${phase.path}/${phase.firstSubpath}`)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  px: 1.5,
                  position: 'relative',
                  bgcolor: pct === 100 ? `${color}22` : 'transparent',
                  transition: 'background-color 0.2s',
                  '&:hover': { bgcolor: `${color}18` },
                  minWidth: 0,
                }}
              >
                {/* Phase label + task pct */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      color: pct === 100 ? color : pct > 0 ? color : 'rgba(255,255,255,0.45)',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {phase.label}
                  </Typography>
                  {isLoaded && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.6rem', color: pct === 100 ? color : 'rgba(255,255,255,0.4)', ml: 0.5, whiteSpace: 'nowrap' }}
                    >
                      {pct}%
                    </Typography>
                  )}
                </Box>

                {/* Task completion bar */}
                <LinearProgress
                  variant={loading && !isLoaded ? 'indeterminate' : 'determinate'}
                  value={isLoaded ? pct : 0}
                  sx={{
                    height: 3,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 },
                  }}
                />

                {/* Timeline elapsed bar */}
                {hasTimeline && (
                  <Box sx={{ mt: 0.6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.04em' }}>
                        TIMELINE
                      </Typography>
                      {tlPct !== null && (
                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', ml: 0.5, whiteSpace: 'nowrap' }}>
                          {tlPct}%
                        </Typography>
                      )}
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={tlPct ?? 0}
                      sx={{
                        height: 3,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '& .MuiLinearProgress-bar': { bgcolor: tlPct !== null ? `${color}88` : 'transparent', borderRadius: 2 },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Tooltip>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default PhaseProgressPipeline;
